<template>
  <div class="fill-height accent">
    <v-container v-if="loadingPage" class="fill-height pa-0 align-start" fluid>
      <v-progress-linear indeterminate></v-progress-linear>
    </v-container>
    <v-container v-else>
      <v-row>
        <v-col>
          <p class="display-1">Perfil de empresa</p>
        </v-col>
      </v-row>
      <div class="mr-8 mb-8" style="position: relative; width: 120px">
        <v-list-item-avatar
          color="white"
          size="130"
          class="mx-0 d-flex justify-center"
          style="box-shadow: 0px 15px 40px rgba(255, 99, 0, 0.18);"
        >
          <v-img
            v-if="profileImageURL || empresa.profile_image"
            :src="profileImageURL ? profileImageURL : `${host}${empresa.profile_image}`"
            aspect-ratio="1"
            lazy-src="@/assets/placeholder-image.gif"
          ></v-img>
          <span v-else class="primary--text display-1 font-weight-bold">{{initials}}</span>
        </v-list-item-avatar>
        <div v-if="profileImageURL">
          <v-btn
            icon
            class="success"
            dark
            :loading="loading"
            :disabled="loading"
            style="position: absolute; bottom: 15px; right: -10px;"
            @click="updateProfileImage()"
          >
            <v-icon small>mdi-check</v-icon>
          </v-btn>
          <v-btn
            icon
            class="error"
            dark
            v-if="!loading"
            style="position: absolute; bottom: 15px; left: 0;"
            @click="profileImageURL = ''"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-btn
          v-else
          icon
          class="primary"
          dark
          style="position: absolute; bottom: 15px; right: -10px;"
          @click="onPickProfileImage"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <input
          type="file"
          class="d-none"
          ref="ProfileImageInput"
          accept="image/*"
          @change="onPickedProfileImage"
        />
      </div>
      <v-row>
        <v-col cols="12" md="6" class="py-1">
          <BaseMyTextField
            :disabled="!editing"
            v-model="empresa.nombre_empresa"
            label="Nombre de empresa *"
            :error-messages="nombreEmpresaErrors"
            @input="$v.empresa.nombre_empresa.$touch()"
            @blur="$v.empresa.nombre_empresa.$touch()"
            required
          ></BaseMyTextField>
        </v-col>
        <v-col cols="12" md="6" class="py-1">
          <BaseMySelect
            :items="industrias"
            :disabled="!editing"
            v-model="empresa.industria"
            label="Industria *"
            menu-props="auto"
            :error-messages="industriaErrors"
            @input="$v.empresa.industria.$touch()"
            @blur="$v.empresa.industria.$touch()"
            required
          ></BaseMySelect>
        </v-col>
        <v-col cols="12" md="4" class="py-1">
          <BaseMyTextField
            :disabled="!editing"
            v-model="empresa.ruc"
            label="Ruc *"
            :error-messages="rucErrors"
            @input="$v.empresa.ruc.$touch()"
            @blur="$v.empresa.ruc.$touch()"
            required
          ></BaseMyTextField>
        </v-col>
        <v-col cols="12" md="4" class="py-1">
          <BaseMySelect
            :items="provincias"
            item-text="name"
            :disabled="!editing"
            v-model="empresa.provincia"
            label="Provincia *"
            :error-messages="provinciaErrors"
            @input="$v.empresa.provincia.$touch()"
            @blur="$v.empresa.provincia.$touch()"
            @change="empresa.ciudad = ''; retriveCities(empresa.provincia);"
            required
          ></BaseMySelect>
        </v-col>
        <v-col cols="12" md="4" class="py-1">
          <BaseMySelect
            :items="ciudades"
            item-text="name"
            :disabled="!editing"
            v-model="empresa.ciudad"
            label="Ciudad *"
            :error-messages="ciudadErrors"
            @input="$v.empresa.ciudad.$touch()"
            @blur="$v.empresa.ciudad.$touch()"
            required
          ></BaseMySelect>
        </v-col>
        <v-col cols="12" md="3" class="py-1">
          <BaseMyTextField
            :disabled="!editing"
            v-model="empresa.codigo_postal"
            label="Código Postal *"
            :error-messages="codigoPostalErrors"
            @input="$v.empresa.codigo_postal.$touch()"
            @blur="$v.empresa.codigo_postal.$touch()"
            required
          ></BaseMyTextField>
        </v-col>
        <v-col cols="12" md="3" class="py-1">
          <BaseMyTextField
            :disabled="!editing"
            v-model="empresa.telefono"
            label="Teléfono *"
            :error-messages="telefonoErrors"
            @input="$v.empresa.telefono.$touch()"
            @blur="$v.empresa.telefono.$touch()"
            required
          ></BaseMyTextField>
        </v-col>
        <v-col cols="12" md="6" class="py-1">
          <BaseMyTextField
            :disabled="!editing"
            v-model="empresa.correo_contacto"
            label="Correo de contacto"
            :error-messages="correoContactoErrors"
            @input="$v.empresa.correo_contacto.$touch()"
            @blur="$v.empresa.correo_contacto.$touch()"
            required
          ></BaseMyTextField>
        </v-col>
        <v-col cols="12" md="8" class="py-1">
          <BaseMyTextField
            :disabled="!editing"
            v-model="empresa.direccion"
            label="Dirección *"
            :error-messages="direccionErrors"
            @input="$v.empresa.direccion.$touch()"
            @blur="$v.empresa.direccion.$touch()"
            required
          ></BaseMyTextField>
        </v-col>
        <v-col cols="12" md="4" class="py-1">
          <BaseMyTextField
            type="number"
            :disabled="!editing"
            v-model="empresa.numero_empleados"
            label="Número de empleados *"
            :error-messages="numeroEmpleadosErrors"
            @input="$v.empresa.numero_empleados.$touch()"
            @blur="$v.empresa.numero_empleados.$touch()"
            required
          ></BaseMyTextField>
        </v-col>
        <v-col cols="12" class="py-1">
          <BaseMyTextArea
            :disabled="!editing"
            v-model="empresa.descripcion"
            label="Descripción"
            rows="3"
          ></BaseMyTextArea>
        </v-col>
        <v-col cols="12" class="d-flex justify-end pt-0">
          <BaseMyButton v-if="!editing" class="ml-2" @click="editing = true">
            <v-icon>mdi-pencil</v-icon>
          </BaseMyButton>
          <template v-else>
            <BaseMyButton color="error" @click="cancelEdit">Cancelar</BaseMyButton>
            <BaseMyButton
              color="success"
              class="ml-2"
              @click="setCompanyProfile"
              :disabled="compareCompany"
            >
              <v-icon>mdi-content-save</v-icon>
            </BaseMyButton>
          </template>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapState } from "vuex";
import industrias from "@/assets/data/industrias.js";

import config from "@/config";

import { validationMixin } from "vuelidate";
import { required, email, sameAs, numeric } from "vuelidate/lib/validators";

export default {
  name: "AdminProfileCompany",
  mixins: [validationMixin],
  validations: {
    empresa: {
      nombre_empresa: { required },
      industria: { required },
      ruc: { required, numeric },
      provincia: { required },
      ciudad: { required },
      codigo_postal: { required },
      telefono: { required, numeric },
      correo_contacto: { email },
      numero_empleados: { required, numeric },
      direccion: { required },
      industria: { required }
    }
  },
  data() {
    return {
      loadingPage: false,
      loading: false,
      empresa: {},
      editing: false,
      provincias: [],
      ciudades: [],
      industrias,
      profileImageURL: "",
      host: config.host
    };
  },
  created() {
    this.retrieveCompany();
  },
  computed: {
    ...mapState("companies/company", ["member", "company"]),
    compareCompany() {
      if (JSON.stringify(this.empresa) === JSON.stringify(this.company))
        return true;
      return false;
    },
    nombreEmpresaErrors() {
      const errors = [];
      if (!this.$v.empresa.nombre_empresa.$dirty) return errors;
      !this.$v.empresa.nombre_empresa.required &&
        errors.push("Campo necesario");
      return errors;
    },
    rucErrors() {
      const errors = [];
      if (!this.$v.empresa.ruc.$dirty) return errors;
      !this.$v.empresa.ruc.required && errors.push("Campo necesario");
      !this.$v.empresa.ruc.numeric && errors.push("Ingrese solo números");
      return errors;
    },
    provinciaErrors() {
      const errors = [];
      if (!this.$v.empresa.provincia.$dirty) return errors;
      !this.$v.empresa.provincia.required && errors.push("Campo necesario");
      return errors;
    },
    ciudadErrors() {
      const errors = [];
      if (!this.$v.empresa.ciudad.$dirty) return errors;
      !this.$v.empresa.ciudad.required && errors.push("Campo necesario");
      return errors;
    },
    codigoPostalErrors() {
      const errors = [];
      if (!this.$v.empresa.codigo_postal.$dirty) return errors;
      !this.$v.empresa.codigo_postal.required && errors.push("Campo necesario");
      return errors;
    },
    telefonoErrors() {
      const errors = [];
      if (!this.$v.empresa.telefono.$dirty) return errors;
      !this.$v.empresa.telefono.required && errors.push("Campo necesario");
      !this.$v.empresa.telefono.numeric && errors.push("Ingrese solo números");
      return errors;
    },
    correoContactoErrors() {
      const errors = [];
      !this.$v.empresa.correo_contacto.email &&
        errors.push("Ingrese un correo valido");
      return errors;
    },
    industriaErrors() {
      const errors = [];
      if (!this.$v.empresa.industria.$dirty) return errors;
      !this.$v.empresa.industria.required && errors.push("Campo necesario");
      return errors;
    },
    direccionErrors() {
      const errors = [];
      if (!this.$v.empresa.direccion.$dirty) return errors;
      !this.$v.empresa.direccion.required && errors.push("Campo necesario");
      return errors;
    },
    numeroEmpleadosErrors() {
      const errors = [];
      if (!this.$v.empresa.numero_empleados.$dirty) return errors;
      !this.$v.empresa.numero_empleados.required &&
        errors.push("Campo necesario");
      !this.$v.empresa.numero_empleados.numeric &&
        errors.push("Ingrese solo números");
      return errors;
    },
    initials() {
      const nombreEmpresa = this.empresa.nombre_empresa;
      const nombreSplit = nombreEmpresa.split(" ");
      if (nombreSplit.length === 1) return nombreEmpresa[0].toUpperCase();
      else
        return `${nombreEmpresa[0].toUpperCase()}${nombreEmpresa[1].toUpperCase()}`;
    }
  },

  methods: {
    onPickProfileImage() {
      this.$refs.ProfileImageInput.click();
    },
    onPickedProfileImage(event) {
      const files = event.target.files;
      const filename = files[0].name;
      if (filename.lastIndexOf(".") <= 0) {
        return alert("Por favor ingrese un archivo valido!");
      }
      if (files[0].size > 2 * 1000 * 1000) {
        this.$store.commit("SET_ALERT", {
          color: "red",
          text: "Peso máximo de foto 2MB",
          visible: true
        });
        return;
      }
      const fileReader = new FileReader();
      fileReader.addEventListener("load", () => {
        this.profileImageURL = fileReader.result;
      });
      fileReader.readAsDataURL(files[0]);
      this.profileImage = files[0];
    },
    updateProfileImage() {
      this.loading = true;
      this.$store
        .dispatch("companies/company/updateCompanyProfileImage", {
          ...this.empresa,
          profile_image: this.profileImage
        })
        .then(data => {
          this.empresa.profile_image = data.profile_image;

          this.editing = false;
          this.profileImageURL = "";

          this.$store.commit("SET_ALERT", {
            color: "green",
            text: "Imagen actualizada",
            visible: true
          });
        })
        .catch(err => {
          this.$store.commit("SET_ALERT", {
            color: "red",
            text: "Error al actualizar la imagen",
            visible: true
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setCompanyProfile() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const data = {
        nombre_empresa: this.empresa.nombre_empresa,
        industria: this.empresa.industria,
        ruc: this.empresa.ruc,
        provincia: this.empresa.provincia,
        ciudad: this.empresa.ciudad,
        codigo_postal: this.empresa.codigo_postal,
        telefono: this.empresa.telefono,
        correo_contacto: this.empresa.correo_contacto,
        direccion: this.empresa.direccion,
        numero_empleados: this.empresa.numero_empleados,
        descripcion: this.empresa.descripcion
      };
      this.$store
        .dispatch("companies/company/updateCompanyProfile", data)
        .then(response => {
          this.editing = false;
          this.$store.commit("SET_ALERT", {
            color: "green",
            text: "Información actualizada",
            visible: true
          });
        })
        .catch(error => {
          this.$store.commit("SET_ALERT", {
            color: "red",
            text: "Error al actualizar información",
            visible: true
          });
        });
    },

    retrieveCompany() {
      this.loadingPage = true;
      this.$store
        .dispatch("companies/company/retrieveCompany")
        .then(response => {
          this.empresa = JSON.parse(
            JSON.stringify({
              nombre_empresa: response.nombre_empresa,
              industria: response.industria,
              ruc: response.ruc,
              provincia: response.provincia,
              ciudad: response.ciudad,
              codigo_postal: response.codigo_postal,
              telefono: response.telefono,
              correo_contacto: response.correo_contacto,
              direccion: response.direccion,
              numero_empleados: response.numero_empleados,
              descripcion: response.descripcion,
              profile_image: response.profile_image
            })
          );
        })
        .finally(() => {
          this.retriveProvincias();
          this.loadingPage = false;
        });
    },

    retriveProvincias() {
      this.$store.dispatch("retriveProvincias", 103).then(data => {
        this.provincias = data;
        if (this.empresa.provincia) this.retriveCities(this.empresa.provincia);
      });
    },

    retriveCities(provinceName) {
      const province = this.provincias.find(
        province => province.name === provinceName
      );

      this.$store
        .dispatch("retriveCities", {
          country: 103,
          province: province.id
        })
        .then(data => {
          this.ciudades = data;
        });
    },

    cancelEdit() {
      this.editing = false;
      this.$v.$reset();
      this.empresa = JSON.parse(JSON.stringify(this.company));
      if (this.empresa.provincia) this.retriveCities(this.empresa.provincia);
    }
  }
};
</script>